const API_BASE = import.meta.env.DEV ? '/api' : 'https://webapi.cmdesk.help/api'

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${endpoint}`
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  }

  // Auth token varsa ekle
  const token = localStorage.getItem('cmdesk_token')
  if (token) {
    (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(url, config)

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Bir hata oluştu' }))
    throw new Error(error.error || `HTTP ${res.status}`)
  }

  return res.json()
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),

  post: <T>(endpoint: string, data?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data?: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string) =>
    request<T>(endpoint, { method: 'DELETE' }),
}

// ==================== TYPE DEFINITIONS ====================

export interface PricingPlan {
  id: string
  name: string
  slug: string
  description: string
  monthlyPrice: number
  yearlyPrice: number
  currency: string
  popular: boolean
  sortOrder: number
  features: PlanFeature[]
}

export interface PlanFeature {
  id: string
  text: string
  included: boolean
  sortOrder: number
}

export interface DownloadLink {
  id: string
  platform: string
  variant: string
  version: string
  fileSize: string
  url: string
  requirements: string | null
  _count: { stats: number }
}

export interface BlogPost {
  id: string
  title: string
  slug: string
  excerpt: string
  content: string
  coverImage: string | null
  category: string
  tags: string | null
  readTime: number
  published: boolean
  featured: boolean
  viewCount: number
  createdAt: string
  author: { id: string; name: string }
}

export interface BlogResponse {
  posts: BlogPost[]
  pagination: Pagination
}

export interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface Career {
  id: string
  title: string
  slug: string
  department: string
  location: string
  type: string
  remote: boolean
  salaryMin: number | null
  salaryMax: number | null
  salaryCurrency: string
  description?: string
  requirements?: string
  benefits?: string
  active?: boolean
  createdAt: string
  _count?: { applications: number }
}

export interface HelpCategory {
  id: string
  name: string
  slug: string
  description: string | null
  icon: string | null
  _count: { articles: number }
}

export interface HelpArticle {
  id: string
  title: string
  slug: string
  content: string
  viewCount: number
  helpful: number
  notHelpful: number
  category: { name: string; slug: string }
}

export interface FAQ {
  id: string
  question: string
  answer: string
  category: string
}

export interface CommunityPost {
  id: string
  title: string
  content: string
  author: string
  category: string
  pinned: boolean
  viewCount: number
  likes: number
  createdAt: string
  _count?: { replies: number }
  replies?: CommunityReply[]
}

export interface CommunityReply {
  id: string
  content: string
  author: string
  likes: number
  createdAt: string
}

export interface ChangelogEntry {
  id: string
  version: string
  type: string
  date: string
  items: ChangelogItem[]
}

export interface ChangelogItem {
  id: string
  text: string
  type: string
}

export interface ContactForm {
  name: string
  email: string
  subject: string
  message: string
}

// ==================== API METHODS ====================

export const pricingApi = {
  getAll: () => api.get<PricingPlan[]>('/pricing'),
  getBySlug: (slug: string) => api.get<PricingPlan>(`/pricing/${slug}`),
}

export const downloadApi = {
  getAll: (platform?: string) =>
    api.get<DownloadLink[]>(`/downloads${platform ? `?platform=${platform}` : ''}`),
  track: (id: string) => api.post(`/downloads/${id}/track`),
}

export const blogApi = {
  getAll: (params?: { category?: string; search?: string; page?: number }) => {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.search) qs.set('search', params.search)
    if (params?.page) qs.set('page', String(params.page))
    return api.get<BlogResponse>(`/blog?${qs}`)
  },
  getFeatured: () => api.get<BlogPost | null>('/blog/featured'),
  getBySlug: (slug: string) => api.get<BlogPost>(`/blog/${slug}`),
}

export const careersApi = {
  getAll: (department?: string) =>
    api.get<Career[]>(`/careers${department && department !== 'all' ? `?department=${department}` : ''}`),
  getBySlug: (slug: string) => api.get<Career>(`/careers/${slug}`),
  apply: (slug: string, data: { name: string; email: string; phone?: string; coverLetter?: string }) =>
    api.post(`/careers/${slug}/apply`, data),
}

export const helpApi = {
  getCategories: () => api.get<HelpCategory[]>('/help/categories'),
  getArticles: (params?: { category?: string; search?: string }) => {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.search) qs.set('search', params.search)
    return api.get<HelpArticle[]>(`/help/articles?${qs}`)
  },
  getPopularArticles: () => api.get<HelpArticle[]>('/help/articles/popular'),
  getArticle: (slug: string) => api.get<HelpArticle>(`/help/articles/${slug}`),
  sendFeedback: (id: string, helpful: boolean) =>
    api.post(`/help/articles/${id}/feedback`, { helpful }),
  getFAQ: (category?: string) =>
    api.get<FAQ[]>(`/help/faq${category ? `?category=${category}` : ''}`),
}

export const communityApi = {
  getAll: (params?: { category?: string; page?: number }) => {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.page) qs.set('page', String(params.page))
    return api.get<{ posts: CommunityPost[]; pagination: Pagination }>(`/community?${qs}`)
  },
  getById: (id: string) => api.get<CommunityPost>(`/community/${id}`),
  create: (data: { title: string; content: string; author: string; category?: string }) =>
    api.post<CommunityPost>('/community', data),
  reply: (id: string, data: { content: string; author: string }) =>
    api.post(`/community/${id}/reply`, data),
  like: (id: string) => api.post(`/community/${id}/like`),
}

export const contactApi = {
  send: (data: ContactForm) => api.post<{ message: string }>('/contact', data),
}

export const newsletterApi = {
  subscribe: (email: string) =>
    api.post<{ message: string; codeSent?: boolean; alreadySubscribed?: boolean; devCode?: string }>(
      '/newsletter/subscribe',
      { email },
    ),
  verify: (email: string, code: string) =>
    api.post<{ message: string; verified?: boolean }>('/newsletter/verify', { email, code }),
  resend: (email: string) =>
    api.post<{ message: string; devCode?: string }>('/newsletter/resend', { email }),
  unsubscribe: (email: string) =>
    api.post<{ message: string }>('/newsletter/unsubscribe', { email }),
}

export const changelogApi = {
  getAll: () => api.get<ChangelogEntry[]>('/changelog'),
  getLatest: () => api.get<ChangelogEntry | null>('/changelog/latest'),
}

// ==================== ADMIN API ====================

export interface AdminUser {
  id: string
  email: string
  name: string
  role: string
  createdAt?: string
}

export interface ContactMessage {
  id: string
  name: string
  email: string
  subject: string
  message: string
  status: string
  adminNote: string | null
  createdAt: string
}

export interface NewsletterSubscriber {
  id: string
  email: string
  active: boolean
  verified: boolean
  subscribedAt: string
}

export interface CareerApplication {
  id: string
  name: string
  email: string
  phone: string | null
  coverLetter: string | null
  resumeUrl: string | null
  status: string
  createdAt: string
}

export interface DownloadStats {
  totalDownloads: number
  platforms: Array<DownloadLink & { _count: { stats: number } }>
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ token: string; admin: AdminUser }>('/auth/login', { email, password }),
  me: () => api.get<AdminUser>('/auth/me'),
}

export const adminPricingApi = {
  getAll: () => api.get<PricingPlan[]>('/pricing'),
  create: (data: Partial<PricingPlan>) => api.post<PricingPlan>('/pricing', data),
  update: (id: string, data: Partial<PricingPlan>) => api.put<PricingPlan>(`/pricing/${id}`, data),
  delete: (id: string) => api.delete(`/pricing/${id}`),
}

export const adminBlogApi = {
  getAll: (params?: { category?: string; search?: string; page?: number }) => {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.search) qs.set('search', params.search)
    if (params?.page) qs.set('page', String(params.page))
    return api.get<BlogResponse>(`/blog?${qs}`)
  },
  create: (data: Partial<BlogPost>) => api.post<BlogPost>('/blog', data),
  update: (id: string, data: Partial<BlogPost>) => api.put<BlogPost>(`/blog/${id}`, data),
  delete: (id: string) => api.delete(`/blog/${id}`),
}

export const adminDownloadApi = {
  getAll: () => api.get<DownloadLink[]>('/downloads'),
  getStats: () => api.get<DownloadStats>('/downloads/stats'),
  create: (data: Partial<DownloadLink>) => api.post<DownloadLink>('/downloads', data),
  update: (id: string, data: Partial<DownloadLink>) => api.put<DownloadLink>(`/downloads/${id}`, data),
  delete: (id: string) => api.delete(`/downloads/${id}`),
}

export const adminCareersApi = {
  getAll: () => api.get<Career[]>('/careers'),
  create: (data: Partial<Career>) => api.post<Career>('/careers', data),
  update: (id: string, data: Partial<Career>) => api.put<Career>(`/careers/${id}`, data),
  delete: (id: string) => api.delete(`/careers/${id}`),
  getApplications: (careerId: string) => api.get<CareerApplication[]>(`/careers/${careerId}/applications`),
}

export const adminHelpApi = {
  getCategories: () => api.get<HelpCategory[]>('/help/categories'),
  createCategory: (data: { name: string; slug: string; description?: string; icon?: string }) =>
    api.post<HelpCategory>('/help/categories', data),
  getArticles: (params?: { category?: string; search?: string }) => {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.search) qs.set('search', params.search)
    return api.get<HelpArticle[]>(`/help/articles?${qs}`)
  },
  createArticle: (data: Partial<HelpArticle>) => api.post<HelpArticle>('/help/articles', data),
  updateArticle: (id: string, data: Partial<HelpArticle>) => api.put<HelpArticle>(`/help/articles/${id}`, data),
  deleteArticle: (id: string) => api.delete(`/help/articles/${id}`),
  getFAQ: () => api.get<FAQ[]>('/help/faq'),
  createFAQ: (data: Partial<FAQ>) => api.post<FAQ>('/help/faq', data),
  updateFAQ: (id: string, data: Partial<FAQ>) => api.put<FAQ>(`/help/faq/${id}`, data),
  deleteFAQ: (id: string) => api.delete(`/help/faq/${id}`),
}

export const adminCommunityApi = {
  getAll: (params?: { page?: number }) => {
    const qs = new URLSearchParams()
    if (params?.page) qs.set('page', String(params.page))
    return api.get<{ posts: CommunityPost[]; pagination: Pagination }>(`/community?${qs}`)
  },
  delete: (id: string) => api.delete(`/community/${id}`),
  pin: (id: string) => api.put<CommunityPost>(`/community/${id}/pin`, {}),
}

export const adminContactApi = {
  getAll: (params?: { status?: string; page?: number }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.page) qs.set('page', String(params.page))
    return api.get<{ messages: ContactMessage[]; pagination: Pagination }>(`/contact?${qs}`)
  },
  updateStatus: (id: string, status: string, adminNote?: string) =>
    api.put<ContactMessage>(`/contact/${id}/status`, { status, adminNote }),
  delete: (id: string) => api.delete(`/contact/${id}`),
}

export const adminNewsletterApi = {
  getAll: () => api.get<{ subscribers: NewsletterSubscriber[]; total: number; activeCount: number; verifiedCount: number }>('/newsletter'),
}

export const adminChangelogApi = {
  getAll: () => api.get<ChangelogEntry[]>('/changelog'),
  create: (data: { version: string; type: string; date: string; items: { text: string; type: string }[] }) =>
    api.post<ChangelogEntry>('/changelog', data),
  update: (id: string, data: Partial<ChangelogEntry>) => api.put<ChangelogEntry>(`/changelog/${id}`, data),
  delete: (id: string) => api.delete(`/changelog/${id}`),
}
